import { useCallback, useEffect, useRef, useState } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';

/** A single photo has nowhere to loop to. */
const canLoop = (amountOfSlides: number) => amountOfSlides > 1;

/*
 * How long the strip has to be still before it counts as settled. Momentum and snapping both fire a
 * scroll event every frame, so a gap this long only happens once the swipe is over. `scrollend`
 * would say it directly, but Safari — most of the phones this is for — does not fire it.
 */
const SETTLE_DELAY_MS = 150;

/*
 * Photos the strip holds ready ahead of the one it shows. The first slide plus this many is what the
 * hero opens with, and swiping keeps the same reach ahead; the rest of the strip is a placeholder
 * until the reader gets near it, so a 100-photo hotel does not ship 100 photo sources up front.
 */
const PHOTOS_AHEAD = 5;

/** Where the strip sits, in slides: they are as wide as the strip, so its width is a slide's. */
const getSlidePosition = (container: HTMLDivElement) => {
  const slideWidth = container.getBoundingClientRect().width;
  return slideWidth > 0 ? container.scrollLeft / slideWidth : 0;
};

/**
 * Makes a strip that ends with a copy of its first photo loop: once a swipe comes to rest on the
 * copy, the strip is put back on the real one without animation. The slides are as wide as the strip,
 * so the two look exactly the same and the reader just carries on swiping from the first photo
 * (MPH-1213).
 *
 * Nothing moves while a finger is on the strip: moving it under an active touch fights the browser's
 * own scrolling, and the touch ending is checked as a settle of its own. A finger landing on the copy
 * before the settle is the one exception: the strip is at rest there, and putting it back first lets
 * that swipe carry on to the second photo instead of bouncing off the end of the strip.
 */
export function useLoopBackToFirstSlide(
  scrollContainerRef: React.MutableRefObject<HTMLDivElement | null> | null,
  amountOfSlides: number,
  enabled: boolean
) {
  const previousAmountRef = useRef(amountOfSlides);

  /*
   * The hotel's own photos replace the few borrowed from the results card, and the copy moves to the
   * new end of the strip. A reader resting on the old copy would find its slot turned into the next
   * real photo, so the strip goes back on the first one before that paints.
   */
  useIsomorphicLayoutEffect(() => {
    const previousAmount = previousAmountRef.current;
    previousAmountRef.current = amountOfSlides;
    const container = scrollContainerRef?.current;
    if (!enabled || !container || previousAmount === amountOfSlides || !canLoop(previousAmount)) return;
    if (Math.round(getSlidePosition(container)) >= previousAmount) container.scroll(0, 0);
  }, [scrollContainerRef, amountOfSlides, enabled]);

  useEffect(() => {
    const container = scrollContainerRef?.current;
    if (!enabled || !container || !canLoop(amountOfSlides)) return undefined;

    let isTouching = false;
    let settleTimer: ReturnType<typeof setTimeout> | undefined;

    const onSettle = () => {
      if (isTouching) return;
      if (Math.round(getSlidePosition(container)) >= amountOfSlides) container.scroll(0, 0);
    };

    const scheduleSettle = () => {
      clearTimeout(settleTimer);
      settleTimer = setTimeout(onSettle, SETTLE_DELAY_MS);
    };

    const onTouchStart = () => {
      isTouching = true;
      clearTimeout(settleTimer);
      // Only when resting right on the copy: partway onto it, the jump would show.
      if (Math.abs(getSlidePosition(container) - amountOfSlides) < 0.01) container.scroll(0, 0);
    };

    const onTouchEnd = () => {
      isTouching = false;
      scheduleSettle();
    };

    container.addEventListener('scroll', scheduleSettle, { passive: true });
    container.addEventListener('touchstart', onTouchStart, { passive: true });
    container.addEventListener('touchend', onTouchEnd, { passive: true });
    container.addEventListener('touchcancel', onTouchEnd, { passive: true });

    return () => {
      clearTimeout(settleTimer);
      container.removeEventListener('scroll', scheduleSettle);
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchend', onTouchEnd);
      container.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [scrollContainerRef, amountOfSlides, enabled]);
}

type SlideImage = { url: string; sources?: { srcSet: string; media?: string }[] };

/**
 * What the mobile hero strip does beyond scrolling, for both heroes (MPH-1213): it holds every photo
 * and loops back to the first, only the photo on show is a tab stop or read by screen readers, the
 * arrow keys move between photos, and photos out of reach are placeholders. `isStrip` is false where the hero lays its photos
 * out side by side instead, and then none of it applies.
 *
 * The caller renders a copy of the first photo after the others when `loop` is set, and hands
 * `onSlideChanged` to the slider overlay, which tracks the photo on show.
 */
export function useHeroStrip(
  scrollContainerRef: React.MutableRefObject<HTMLDivElement | null>,
  amountOfSlides: number,
  isStrip: boolean
) {
  const { localization } = usePageWrapperData();
  const [visibleSlide, setVisibleSlide] = useState(0);
  const [readyUpTo, setReadyUpTo] = useState(PHOTOS_AHEAD);
  const loop = isStrip && canLoop(amountOfSlides);

  useLoopBackToFirstSlide(scrollContainerRef, amountOfSlides, loop);

  const onSlideChanged = useCallback((slide: number) => {
    setVisibleSlide(slide);
    setReadyUpTo((previous) => Math.max(previous, slide + PHOTOS_AHEAD));
  }, []);

  const isPhotoReady = (index: number) => !isStrip || index <= readyUpTo;

  const getSlideImage = (index: number, image: SlideImage): SlideImage =>
    isPhotoReady(index) ? image : { url: `${localization.imageBasePath}empty_placeholder.svg` };

  /*
   * Only the photo on show is a tab stop, as in `ImageSlider` (MPH-243), and the others are hidden
   * from screen readers too: `tabIndex` alone leaves every photo in what VoiceOver and TalkBack read
   * out ahead of the hotel's name. The rest stay reachable through the arrow keys, the counter and
   * the gallery.
   */
  const getSlideAccessibility = (index: number) =>
    !isStrip || index === visibleSlide ? { tabIndex: 0 } : { tabIndex: -1, 'aria-hidden': true };

  const onSlideKeyDown = (index: number) => (event: React.KeyboardEvent) => {
    const container = scrollContainerRef.current;
    if (!isStrip || !container || (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft')) return;
    event.preventDefault();

    const target = index + (event.key === 'ArrowRight' ? 1 : -1);
    if (target < 0 || target > (loop ? amountOfSlides : amountOfSlides - 1)) return;

    container.scrollTo({ left: container.getBoundingClientRect().width * target, behavior: 'smooth' });
    // The copy ending a looping strip is no tab stop: focus the photo it repeats, where the strip
    // lands once it settles, so what has focus and what is on show agree. Counted as on show right
    // away rather than once the scroll gets there, so focus never lands on a hidden photo.
    const photo = target % amountOfSlides;
    onSlideChanged(photo);
    (container.children[photo] as HTMLElement | undefined)?.focus({ preventScroll: true });
  };

  return { loop, onSlideChanged, isPhotoReady, getSlideImage, getSlideAccessibility, onSlideKeyDown };
}
